// ADR 23.9.26y §1 — can this browser hold a daily reminder, and if not, why.
//
// Pure over its inputs, so the whole gate table is a unit test. All three
// platform readers are W1's (23.9.26u §8) and none is redefined here: wherever
// the reminder row offers "Show me how", W1's `installAffordance` would offer
// `ios_instructions` too, so the row never sends a learner to steps W1
// withholds.

import { displayModeOf } from '../../pwa/displayMode'
import { isInAppBrowser, platformOf } from '../../pwa/installAffordance'

export type PushSupport = 'supported' | 'install_first' | 'unsupported'

export interface PushEnv {
  hasServiceWorker: boolean
  hasPushManager: boolean
  hasNotification: boolean
  ios: boolean
  standalone: boolean
  inAppBrowser: boolean
}

/**
 * §1's table, first match wins. An iOS browser tab exposes no `PushManager`
 * at all, so on iOS the missing API is the expected symptom of not being
 * installed, not the verdict — `standalone` is read before the APIs.
 */
export function pushSupport(env: PushEnv): PushSupport {
  if (env.inAppBrowser) return 'unsupported'
  if (env.ios && !env.standalone) return 'install_first'
  const apis = env.hasServiceWorker && env.hasPushManager && env.hasNotification
  return apis ? 'supported' : 'unsupported'
}

/** The one impure reader. */
export function readPushEnv(): PushEnv {
  const ua = navigator.userAgent
  return {
    hasServiceWorker: 'serviceWorker' in navigator,
    hasPushManager: 'PushManager' in window,
    hasNotification: typeof Notification !== 'undefined',
    ios: platformOf(ua, navigator.maxTouchPoints ?? 0) === 'ios',
    standalone: displayModeOf(window) === 'standalone',
    inAppBrowser: isInAppBrowser(ua),
  }
}

/** W4a's base64url VAPID public key → the bytes `pushManager.subscribe` takes. */
export function vapidKeyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const padded = base64url.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(base64url.length / 4) * 4, '=')
  const raw = atob(padded)
  const bytes = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i)
  return bytes
}

/** Is a subscription's `applicationServerKey` exactly these bytes? `null` never is. */
export function sameKey(a: ArrayBuffer | null, b: Uint8Array): boolean {
  if (a === null || a.byteLength !== b.length) return false
  const view = new Uint8Array(a)
  return view.every((byte, i) => byte === b[i])
}
